import type { ContextType } from "react";
import { PureComponent } from "react";

import get from "lodash/get";
import isFunction from "lodash/isFunction";
import map from "lodash/map";

import { ConversionSegmentsUtils } from "app/pages/conversion/ConversionSegmentsUtils";

import type { ISegmentsData } from "../../../../../app/services/conversion/ConversionSegmentsService";
import { LinkContext } from "../../../../components/WithLink/src/context";
import WithTranslation from "../../../../components/WithTranslation/src/WithTranslation";
import { conversionVerticals } from "../../constants/conversionVerticals";
import type { IScatterItem } from "../../types/conversionTypes";

import type { IScatterData, IScatterMetric } from "./ConversionScatterChart";
import { ConversionScatterChart } from "./ConversionScatterChart";

interface IConversionCategoryScatterProps {
    scatterXVertical: string;
    scatterYVertical: string;
    benchmarkEnabled: boolean;
    segmentsData: ISegmentsData;
    data: {
        Data: IScatterItem[];
        Interval?: {
            units: string;
            startyear: number;
            start: number;
            count: number;
            startdate: string;
            enddate: string;
        };
        Domains?: string[];
    };
    filters: any;
}

export class ConversionCategoryScatter extends PureComponent<IConversionCategoryScatterProps> {
    static contextType = LinkContext;

    context!: ContextType<typeof LinkContext>;

    private translate: any;

    public scatterMetricsTransform(data: any): { x: IScatterMetric; y: IScatterMetric } {
        // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'IConversionVerticals' — legacy code uses string keys
        const scatterXVertical = conversionVerticals[this.props.scatterXVertical];
        // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'IConversionVerticals' — legacy code uses string keys
        const scatterYVertical = conversionVerticals[this.props.scatterYVertical];
        const scatterItems = get(data, "Data", []);
        const xMidValue = isFunction(scatterXVertical.midValueCalculator)
            ? scatterXVertical.midValueCalculator(scatterItems)
            : undefined;
        const yMidValue = isFunction(scatterYVertical.midValueCalculator)
            ? scatterYVertical.midValueCalculator(scatterItems)
            : undefined;

        return {
            x: {
                name: this.translate(scatterXVertical.title),
                valueFormat: (value) => scatterXVertical.filter[0]()(value, scatterXVertical.filter[1]),
                highLabel: this.translate("conversion.scatter.high"),
                lowLabel: this.translate("conversion.scatter.low"),
                midValue: xMidValue,
            },
            y: {
                name: this.translate(scatterYVertical.title),
                valueFormat: (value) => scatterYVertical.filter[0]()(value, scatterYVertical.filter[1]),
                highLabel: this.translate("conversion.scatter.high"),
                lowLabel: this.translate("conversion.scatter.low"),
                midValue: yMidValue,
            },
        };
    }

    public scatterCommonConfigTransform(): any {
        const props = this.props;
        return {
            xAxis: {
                title: {
                    // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — legacy code uses string keys
                    text: `${this.translate(conversionVerticals[props.scatterXVertical].title)} (${
                        // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — legacy code uses string keys
                        conversionVerticals[props.scatterXVertical].metricLabel
                    })`,
                },
                labels: {
                    formatter(this: FixmeType) {
                        // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — legacy code uses string keys
                        return conversionVerticals[props.scatterXVertical].filter[0]()(
                            this.value,
                            // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — legacy code uses string keys
                            conversionVerticals[props.scatterXVertical].filter[1],
                        );
                    },
                },
            },
            yAxis: {
                title: {
                    // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — legacy code uses string keys
                    text: `${this.translate(conversionVerticals[props.scatterYVertical].title)} (${
                        // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — legacy code uses string keys
                        conversionVerticals[props.scatterYVertical].metricLabel
                    })`,
                },
                labels: {
                    formatter(this: FixmeType) {
                        // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — legacy code uses string keys
                        return conversionVerticals[props.scatterYVertical].filter[0]()(
                            this.value,
                            // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — legacy code uses string keys
                            conversionVerticals[props.scatterYVertical].filter[1],
                        );
                    },
                },
            },
        };
    }

    public scatterChartDataTransform(data: any, filters: any): IScatterData[] | undefined {
        if (!data || !data.Data) {
            return undefined;
        }
        const { country, gid } = filters;
        const res = map(data.Data, (dataItem) => {
            const segmentData = ConversionSegmentsUtils.getSegmentById(this.props.segmentsData, dataItem.SegmentId);
            return {
                name: dataItem.Domain,
                icon: dataItem.Favicon,
                singleLob: segmentData ? segmentData.isSingleLob : true,
                segmentName: segmentData ? segmentData.segmentName : undefined,
                link: this.context?.linkFn("conversion-customsegement", {
                    country,
                    gid,
                    sid: dataItem.SegmentId,
                    duration: "6m",
                    comparedDuration: "12m",
                }),
                data: [
                    {
                        // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — legacy code uses string keys
                        x: dataItem[conversionVerticals[this.props.scatterXVertical].dataKey],
                        // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — legacy code uses string keys
                        y: dataItem[conversionVerticals[this.props.scatterYVertical].dataKey],
                    },
                ],
            };
        });
        return res;
    }

    public render() {
        const { data, benchmarkEnabled, filters } = this.props;

        return (
            <WithTranslation>
                {(translate) => {
                    this.translate = translate;

                    return (
                        <ConversionScatterChart
                            data={this.scatterChartDataTransform(data, filters) ?? []}
                            metrics={this.scatterMetricsTransform(data)}
                            config={this.scatterCommonConfigTransform()}
                            options={{ efficiencyZones: benchmarkEnabled }}
                            translate={translate}
                        />
                    );
                }}
            </WithTranslation>
        );
    }
}
